/**
 * noew subscription guard — a subscription only makes sense once per cart.
 *
 * Elements marked [data-noew-once] (with data-once-variant="<variant id>"):
 * - AJAX product forms: the submit button is disabled with an "Already in
 *   cart" label while that variant is in the cart; re-enables on removal
 *   (driven by the theme's `cart:update` event, which also fires on add).
 * - Direct-to-checkout forms (they carry a return_to input): never disabled —
 *   the label swaps (e.g. "Subscription in cart") and, if the variant is
 *   already in the cart, submitting skips the add and goes straight to
 *   checkout instead (prevents quantity 2+).
 * data-once-added-label overrides the swapped label per element.
 */

const ONCE_SELECTOR = '[data-noew-once]';
const ADDED_LABEL = 'Already in cart';

const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

async function fetchCart() {
  return (await fetch(`${rootUrl}cart.js`, { cache: 'no-store' })).json();
}

async function fetchCartVariantIds() {
  const cart = await fetchCart();
  return new Set(cart.items.map((item) => String(item.variant_id)));
}

function isCheckoutForm(guard) {
  return guard.matches('form') && guard.querySelector('input[name="return_to"]');
}

async function refreshOnceGuards() {
  const guards = document.querySelectorAll(ONCE_SELECTOR);
  if (!guards.length) return;

  let variantsInCart;
  try {
    variantsInCart = await fetchCartVariantIds();
  } catch {
    return;
  }

  for (const guard of guards) {
    const checkoutForm = isCheckoutForm(guard); // stays clickable: submit forwards to checkout

    const button = guard.querySelector('button[type="submit"]');
    if (!button) continue;
    const label =
      button.querySelector('.add-to-cart-text__content span span') || button.querySelector('.noew-btn__label');

    const added = variantsInCart.has(guard.dataset.onceVariant);

    if (added && guard.dataset.onceState !== 'added') {
      guard.dataset.onceState = 'added';
      if (label) {
        guard.dataset.onceOriginalLabel = label.textContent;
        label.textContent = guard.dataset.onceAddedLabel || ADDED_LABEL;
      }
      if (!checkoutForm) button.disabled = true;
    } else if (!added && guard.dataset.onceState === 'added') {
      delete guard.dataset.onceState;
      if (label && guard.dataset.onceOriginalLabel) label.textContent = guard.dataset.onceOriginalLabel;
      if (!checkoutForm) button.disabled = false;
    }
  }
}

function wireCheckoutForms() {
  for (const form of document.querySelectorAll(`form${ONCE_SELECTOR}`)) {
    if (!isCheckoutForm(form) || form.dataset.onceWired) continue;
    form.dataset.onceWired = 'true';
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      let alreadyInCart = false;
      try {
        alreadyInCart = (await fetchCartVariantIds()).has(form.dataset.onceVariant);
      } catch {
        // cart unavailable — fall through to the normal add
      }
      if (alreadyInCart) {
        window.location.href = `${rootUrl}checkout`;
      } else {
        form.submit(); // native submit skips this listener
      }
    });
  }
}

/**
 * bfcache: navigating BACK from checkout restores the page's pre-checkout DOM
 * — the CTA label and the header cart bubble both go stale (the native
 * form-post add never fired a cart:update, so the theme's own restore check
 * has nothing to compare against). Re-sync both from the live cart.
 */
async function syncCartBubble() {
  try {
    const cart = await fetchCart();
    const icon = document.querySelector('cart-icon');
    if (icon && typeof icon.renderCartBubble === 'function') {
      icon.renderCartBubble(cart.item_count, false, false);
    }
  } catch {
    // cart unavailable — leave the bubble as-is
  }
}

window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  refreshOnceGuards();
  syncCartBubble();
});

document.addEventListener('cart:update', () => refreshOnceGuards());
wireCheckoutForms();
refreshOnceGuards();
